import { useState } from "react";
import { useNavigate } from "react-router";
import { DropdownMenu } from "radix-ui";
import {
  Check,
  ChevronDown,
  Keyboard,
  KeyRound,
  LogOut,
  Monitor,
  Moon,
  Sun,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";

import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Kbd } from "@/components/ui/Kbd";
import { ApiError } from "@/lib/api/client";
import { authApi } from "@/features/auth/api";
import { useAuth } from "@/lib/auth/AuthProvider";
import { showShortcuts } from "@/lib/shortcuts";
import { useTheme, type Theme } from "@/lib/theme";

/** One item in the account menu: a soft tile, not a row in a list. */
const ITEM =
  "flex cursor-pointer items-center gap-2.5 rounded-xs px-3 py-2 text-base text-fg " +
  "outline-none data-[highlighted]:bg-ink-100";

const THEMES: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "Match this device", icon: Monitor },
];

/** Who is signed in, and the few things they might want to do about it. */
export function AccountMenu() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [changingPassword, setChangingPassword] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useTheme();

  if (!session) return null;

  async function changePassword() {
    setError(null);
    setSaving(true);
    try {
      await authApi.changePassword(current, next);
      toast.success("Password changed. Sign in again.");
      await signOut();
      navigate("/login", { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not change the password.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2 text-base text-fg transition-colors duration-150 hover:bg-ink-900/[0.07]"
          >
            <Avatar
              name={session.user.full_name_en}
              fileId={session.user.profile_picture_file_id}
              size="sm"
              className="rounded-full"
            />
            <span className="hidden max-w-40 truncate font-medium sm:inline">
              {session.user.full_name_en}
            </span>
            <ChevronDown className="size-4 text-fg-subtle" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={8}
            className="z-50 min-w-60 rounded-md bg-surface-raised p-1.5 shadow-pop ring-1 ring-line"
          >
            <div className="px-3 pb-2.5 pt-2">
              <p className="truncate text-base font-semibold text-fg">
                {session.user.full_name_en}
              </p>
              <p className="truncate text-xs text-fg-subtle">{session.user.email}</p>
              {session.user.employee_id ? (
                <p className="u-ident mt-0.5 text-xs text-fg-subtle">
                  {session.user.employee_id}
                </p>
              ) : null}
            </div>

            <DropdownMenu.Separator className="mx-1 mb-1.5 h-px bg-line-faint" />

            <DropdownMenu.Item onSelect={() => navigate("/profile")} className={ITEM}>
              <UserRound className="size-4 text-fg-subtle" aria-hidden="true" />
              My profile
            </DropdownMenu.Item>

            <DropdownMenu.Item onSelect={() => setChangingPassword(true)} className={ITEM}>
              <KeyRound className="size-4 text-fg-subtle" aria-hidden="true" />
              Change password
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="mx-1 my-1.5 h-px bg-line-faint" />

            <DropdownMenu.Label className="px-3 pb-1 text-2xs font-semibold text-fg-subtle">
              Theme
            </DropdownMenu.Label>
            {THEMES.map((one) => (
              <DropdownMenu.Item
                key={one.value}
                role="menuitemradio"
                aria-checked={theme === one.value}
                // Stays open, so the change can be seen before it is kept.
                onSelect={(event) => {
                  event.preventDefault();
                  setTheme(one.value);
                }}
                className={ITEM}
              >
                <one.icon className="size-4 text-fg-subtle" aria-hidden="true" />
                <span className="flex-1">{one.label}</span>
                {theme === one.value ? (
                  <Check className="size-4 text-accent" aria-hidden="true" />
                ) : null}
              </DropdownMenu.Item>
            ))}

            <DropdownMenu.Separator className="mx-1 my-1.5 h-px bg-line-faint" />

            {/* The sheet is the shell's, so this only asks for it; the key
                that does the same is printed beside it. */}
            <DropdownMenu.Item onSelect={() => showShortcuts()} className={ITEM}>
              <Keyboard className="size-4 text-fg-subtle" aria-hidden="true" />
              <span className="flex-1">Keyboard shortcuts</span>
              <Kbd>?</Kbd>
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="mx-1 my-1.5 h-px bg-line-faint" />

            <DropdownMenu.Item
              onSelect={async () => {
                await signOut();
                navigate("/login", { replace: true });
              }}
              className={ITEM}
            >
              <LogOut className="size-4 text-fg-subtle" aria-hidden="true" />
              Sign out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <Dialog
        open={changingPassword}
        onOpenChange={(open) => {
          setChangingPassword(open);
          if (!open) {
            setCurrent("");
            setNext("");
            setError(null);
          }
        }}
        title="Change your password"
        description="You will be signed out on every device."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setChangingPassword(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={changePassword} loading={saving}>
              Change password
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {error ? (
            <p className="rounded-sm bg-alert-50 px-3 py-2 text-base text-alert-700">{error}</p>
          ) : null}
          <Field label="Current password">
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>
          <Field label="New password" hint="At least 10 characters">
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </Field>
        </div>
      </Dialog>
    </>
  );
}
